import { invalidateMinicart } from "~/app/store/bffApi";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { toMoxyDateNullable, type MoxyDateString } from "~/common/types/MoxyDate";
import { type LineItem } from "../models/CartData";
import { occBaseSiteUrl } from "~/app/api/occ/occFetch";
import { occCartUserPath } from "~/app/api/occ/occUserPath";
import { ApiCallPriority, apiPATCH } from "~/app/api/apiCallsService";
import { BatchSizeProvider } from "~/app/api/batchSizeProvider";
import { type QueuedCall, registerCallProcessor, scheduleCall } from "~/features/cart/cart/api/CartService";
import { getStore } from "~/app/store/getStore";
import { naiveTrackCartContents } from "~/app/analytics/ga/abandonment";
import { getCurrentBrowserTimezone } from "~/common/utils/dateUtils";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { resolveActiveCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";

export interface UpdatePackageEntryItem {
  id: number;
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;
  comment?: string;
  selectedDeliveryDate: Date | null;
  selectedDeliveryDateIsManual: boolean;
  estimatedDeliveryDate?: Date;
  edd?: Date;
  primaryWarehouseCode?: string;
  packageEntryPk: number;
  orderEntryPk: number;

  priceReference: string;

  // Quote specific fields,
  quoteExpiryDate?: Date | null;
  logisticalComment?: string;
  estimatedShippingDate?: Date | null;
  optionalParts?: UpdatePackageEntryPartItem[];
  customerOrderNumber?: string;
  originCountry: string;

  salesCampaignCode?: string;

  // Switch to newest product version
  successorProductCode?: string;

  //added Target Price
  targetPrice?: number;
}

export interface UpdatePackageEntryPartItem {
  code: string;
  productCode: string;
  selectedDeliveryDate: Date | null;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  comment: string;
  originCountry: string;
}

interface UpdatePackageEntryApiItem {
  id: number;
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;
  comment?: string;
  selectedDeliveryDate: MoxyDateString | null;
  selectedDeliveryDateIsManual: boolean;
  estimatedDeliveryDate: MoxyDateString | null;
  //edd: string;
  primaryWarehouseCode?: string;
  packageEntryPk: number;
  orderEntryPk: number;
  priceReference: string;

  // Target price is optional and sent as a scalar value
  targetPrice?: number;

  // Quote specific fields,
  quoteExpiryDate?: MoxyDateString | null;
  logisticalComment?: string;
  estimatedShippingDate?: MoxyDateString | null;
  optionalParts?: UpdatePackageEntryPartApiItem[];
  customerOrderNumber?: string;
  originCountry: string;

  salesCampaignCode?: string;
}

export interface UpdatePackageEntryPartApiItem {
  code: string;
  productCode: string;
  selectedDeliveryDate: MoxyDateString | null;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  comment: string;
  originCountry: string;
}

interface UpdatePackageEntryPayload {
  items: UpdatePackageEntryApiItem[];
  userTimeZone?: string;
  switchProductsVersions?: boolean;
}

export function transformUpdateApiItem(item: UpdatePackageEntryItem): UpdatePackageEntryApiItem {
  return {
    ...item,
    selectedDeliveryDate: toMoxyDateNullable(item.selectedDeliveryDate),
    quoteExpiryDate: toMoxyDateNullable(item.quoteExpiryDate),
    estimatedShippingDate: toMoxyDateNullable(item.estimatedShippingDate),
    estimatedDeliveryDate: toMoxyDateNullable(item.edd),
    optionalParts: item.optionalParts ? item.optionalParts.map(transformOptionalItem) : undefined,
  };
}

function transformOptionalItem(item: UpdatePackageEntryPartItem): UpdatePackageEntryPartApiItem {
  return {
    ...item,
    selectedDeliveryDate: toMoxyDateNullable(item.selectedDeliveryDate),
  };
}

export function fromLineItem(data: LineItem, _subLineItems?: LineItem[]): UpdatePackageEntryItem {
  return {
    id: data.id,
    productCode: data.productCode,
    selectedDeliveryDate: data.deliveryDate,
    selectedDeliveryDateIsManual: data.manuallySetRDD,
    comment: data.comment,
    quantity: data.quantity,
    packageCode: data.packCode,
    packageSize: data.packSize,
    primaryWarehouseCode: data.primaryWarehouseCode,
    packageEntryPk: data.pk,
    orderEntryPk: data.cartEntryPk,
    priceReference: data.priceReference,
    quoteExpiryDate: data.quoteExpiryDate,
    logisticalComment: data.logisticalComment,
    estimatedShippingDate: data.estimatedShippingDate,
    edd: data.edd?.date,
    customerOrderNumber: data.customerOrderNumber,
    optionalParts: undefined,
    originCountry: data.countryOfOrigin,

    salesCampaignCode: data.saleCampaignCode,
    targetPrice: data.targetPrice?.value,

    successorProductCode: data.successorProductCode,
  };
}

export function sendUpdate(item: UpdatePackageEntryItem) {
  return sendBulkUpdate([item]);
}

export function sendBulkUpdate(items: UpdatePackageEntryItem[]) {
  if (!getRuntimeConfig().batching.lineItemUpdate.enabled) {
    return scheduleCall("update", { items });
  }

  const queueElement: Partial<QueueElement> = { apiData: items };
  const cacheEntry = new Promise((resolve, reject) => {
    queueElement.resolve = resolve;
    queueElement.reject = reject;
  });
  const finalizedQueueElement = queueElement as QueueElement;
  queuedCalls.push(finalizedQueueElement);
  scheduleCall("update", { items: [] });

  return cacheEntry;
}

interface QueueElement {
  resolve: (res: Response) => void;
  reject: (err: unknown) => void;
  apiData: UpdatePackageEntryItem[];
}
const queuedCalls: QueueElement[] = [];
// BatchSizeProvider is stateful; construct lazily so it observes the bootstrap-populated config.
let lineItemBatchSizeProvider: BatchSizeProvider | null = null;
const getLineItemBatchSizeProvider = () => {
  if (!lineItemBatchSizeProvider) {
    lineItemBatchSizeProvider = new BatchSizeProvider(getRuntimeConfig().batching.lineItemUpdate.batches);
  }
  return lineItemBatchSizeProvider;
};

function getBatchedItems() {
  const itemsToCall = queuedCalls.slice(0, getLineItemBatchSizeProvider().getBatchSize());
  queuedCalls.splice(0, itemsToCall.length);
  return itemsToCall;
}

registerCallProcessor({
  type: "update",
  canBeExecuted: (ongoingCalls: QueuedCall[]) => {
    const isDeleteOngoing = ongoingCalls.some((call) => call.type === "delete" || call.type === "deleteCart");
    return !isDeleteOngoing;
  },
  execute: async ({ items }: { items: UpdatePackageEntryItem[] }) => {
    if (!getRuntimeConfig().batching.lineItemUpdate.enabled) {
      if (items.length === 0) {
        return new Promise((resolve) => resolve(new Response()));
      }

      const state = getStore().getState();
      let switchProductsVersions = false;

      const transformedData = items
        .map((item) => ({ item, storeLineItem: state.cart.packageEntriesById[item.id] }))
        .filter(({ item, storeLineItem }) => storeLineItem)
        .map(({ item }) => {
          if (item.successorProductCode) {
            switchProductsVersions = true;
          }
          return transformUpdateApiItem(item);
        });

      const payload: UpdatePackageEntryPayload = {
        items: transformedData,
        userTimeZone: getCurrentBrowserTimezone(),
      };

      if (switchProductsVersions) {
        payload["switchProductsVersions"] = switchProductsVersions;
      }
      const body = JSON.stringify(payload);

      return apiPATCH(
        await resolveBulkUpdateApiUrl(),
        {
          body,
          cache: "no-cache",
          headers: {
            dataType: "json",
            "content-type": "application/json",
            CSRFToken: getCsrfValue(),
          },
          redirect: "follow",
        },
        ApiCallPriority.NORMAL,
      ).then((result) => {
        invalidateMinicart();
        return result;
      });
    }

    const batchedItems = getBatchedItems();
    let switchProductsVersions = false;

    if (batchedItems.length === 0) {
      return new Promise((resolve) => resolve(new Response()));
    }

    const apiItems = batchedItems.reduce((arr, item) => arr.concat(...item.apiData), [] as UpdatePackageEntryItem[]);

    const state = getStore().getState();
    const transformedData = apiItems
      .map((item) => ({ item, storeLineItem: state.cart.packageEntriesById[item.id] }))
      .filter(({ item, storeLineItem }) => storeLineItem)
      .map(({ item }) => {
        if (item.successorProductCode) {
          switchProductsVersions = true;
        }
        return transformUpdateApiItem(item);
      });

    if (batchedItems.length === 0) {
      return new Promise((resolve) => resolve(new Response()));
    }

    const payload: UpdatePackageEntryPayload = {
      items: transformedData,
      userTimeZone: getCurrentBrowserTimezone(),
    };

    if (switchProductsVersions) {
      payload["switchProductsVersions"] = switchProductsVersions;
    }
    const body = JSON.stringify(payload);

    try {
      const result = await apiPATCH(
        await resolveBulkUpdateApiUrl(),
        {
          body,
          cache: "no-cache",
          headers: {
            dataType: "json",
            "content-type": "application/json",
            CSRFToken: getCsrfValue(),
          },
          redirect: "follow",
        },
        ApiCallPriority.NORMAL,
      );
      invalidateMinicart();
      naiveTrackCartContents();
      batchedItems.forEach((item) => item.resolve(result));
      return result;
    } catch (error) {
      batchedItems.forEach((item) => item.reject(error));
      throw error;
    }
  },
});

export async function resolveBulkUpdateApiUrl(): Promise<string> {
  const activeCartId = await resolveActiveCartResourceId();
  if (!activeCartId) {
    throw new Error("Cannot update cart entries without an explicit Active Cart identity");
  }
  return occBaseSiteUrl(`${occCartUserPath()}/carts/${encodeURIComponent(activeCartId)}/entries/bulk`);
}
